(function () {
  class ShopTheLook extends HTMLElement {
    constructor() {
      super();
      this.cacheElements();
      this.setInitialState();
    }

    connectedCallback() {
      this.setupEventListeners();
      this.setupScrollObserver();
      this.setupResizeListener();
      this.setDrawerState("closed");
      this.setupMobileSupport();
      this.forceCenterViewAll();
    }

    disconnectedCallback() {
      this.cleanupObserversAndListeners();
    }

    cacheElements() {
      this.drawer = this.querySelector(".shop-the-look__drawer");
      this.hotspots = this.querySelectorAll(".shop-the-look__hotspot");
      this.products = this.querySelectorAll(".shop-the-look__product");
      this.viewAllButton = this.querySelector(".shop-the-look__view-all-button");
      this.productList = this.querySelector(".shop-the-look__product-list");
      this.productGrid = this.drawer;
      this.viewAllContainer = this.querySelector(".shop-the-look__view-all");
      this.isMobile = window.innerWidth < 1025;
    }

    setInitialState() {
      this.activeHotspot = null;
      this.isDrawerExpanded = false;
      this.observer = null;
      this.userInteracted = false;
      this.isGridView = false;
      this._docClickHandlerAdded = false;
      this._visibleTimeout = null;
      this._visibleTimerValue = parseInt(this.getAttribute('data-visible-timer'), 10) || 1500;
    }

    setupResizeListener() {
      this.handleResizeBound = this.handleResize.bind(this);
      window.addEventListener("resize", this.handleResizeBound);
    }

    setupMobileSupport() {
      const imageContainer = this.querySelector(".shop-the-look__image-container");
      imageContainer?.addEventListener("click", () => {
        this.closeDrawer();
        this.closeDrawerAccessibility();
        this.forceCenterViewAll();
      });

      if (this.isMobile) {
        this.setupMobileSwipeToClose();
      }
    }

    cleanupObserversAndListeners() {
      this.observer?.disconnect();
      window.removeEventListener("resize", this.handleResizeBound);
      if (this._docClickHandler) {
        document.removeEventListener("click", this._docClickHandler, true);
      }
    }

    handleResize() {
      const wasMobile = this.isMobile;
      this.isMobile = window.innerWidth < 1025;
      if (wasMobile !== this.isMobile) {
        this.resetDrawer();
        if (this.isMobile) {
          this.setupMobileSwipeToClose();
        }
      }
      this.forceCenterViewAll();
    }

    setupScrollObserver() {
      this.observer?.disconnect();
      if (this.isMobile) return;
      let wasVisible = false;

      this.observer = new IntersectionObserver(entries => {
        entries.forEach(entry => {
          const isVisible = entry.intersectionRatio >= 0.35;

          if (isVisible && !wasVisible && !this.userInteracted) {
            this.setDrawerState("visible");
            wasVisible = true;
            this.startVisibleTimer();
          } else if (!isVisible && !this.drawer.classList.contains("expanded")) {
            this.resetAllStates();
            wasVisible = false;
            this.userInteracted = false;
            this.clearVisibleTimer();
          }
        });
      }, { threshold: [0, 0.75] });

      this.observer.observe(this);
    }

    setupEventListeners() {
      this.setupHotspotListeners();
      this.setupProductListeners();
      this.setupViewAllListener();
      this.setupDrawerCloseListener();
      this.setupAccessibilityClose();
    }

    setupHotspotListeners() {
      this.hotspots.forEach(hotspot => {
        hotspot.addEventListener("pointerup", e => {
          e.preventDefault();
          e.stopPropagation();
          this._suppressDocClick = true;
          setTimeout(() => this._suppressDocClick = false, 150);

          this.activateHotspot(hotspot);
          this.userInteracted = true;
          this.observer?.disconnect();
          this.setupScrollObserver();
          this.openDrawer();
          this.openDrawerAccessibility(hotspot);
        });
      });
    }

    activateHotspot(hotspot) {
      this.hotspots.forEach(h => {
        h.classList.remove("active");
        h.setAttribute("aria-pressed", "false");
      });
      hotspot.classList.add("active");
      hotspot.setAttribute("aria-pressed", "true");
      this.activeHotspot = hotspot;
      this.isGridView = false;

      const index = parseInt(hotspot.dataset.hotspotIndex, 10) - 1;
      const firstCol = this.querySelector(".shop-the-look__product-list-col");
      if (firstCol) {
        const products = firstCol.querySelectorAll(".shop-the-look__product");
        products.forEach((p, i) => {
          p.classList.toggle("active", i === index);
        });
      }
    }

    setupProductListeners() {
      this.products.forEach(product => {
        const link = product.querySelector(".shop-the-look__product-link");
        const image = product.querySelector(".shop-the-look__product-image");
        image?.addEventListener("click", e => {
          e.preventDefault();
          if (link?.href) window.location.href = link.href;
        });
      });
    }

    setupViewAllListener() {
      if (!this.viewAllButton) return;

      this.viewAllText = this.viewAllButton.dataset.viewAllText || "View All Products";
      this.viewLessText = this.viewAllButton.dataset.viewLessText || "Close";

      this.viewAllButton.onclick = null;
      this.viewAllButton.addEventListener("pointerup", e => {
        e.preventDefault();
        e.stopImmediatePropagation();
        this._suppressDocClick = true;
        setTimeout(() => this._suppressDocClick = false, 150);

        this.userInteracted = true;
        this.observer?.disconnect();
        this.setupScrollObserver();

        this.isGridView ? this.closeDrawer() : this.expandDrawer();
        this.openDrawerAccessibility(this.viewAllButton);
      });
    }

    setupDrawerCloseListener() {
      const closeButton = this.drawer.querySelector(".shop-the-look__drawer-close");
      closeButton?.addEventListener("click", e => {
        e.preventDefault();
        e.stopImmediatePropagation();
        if (this.drawer.getAttribute('data-drawer-state') !== 'closed') {
          this.closeDrawer();
          this.closeDrawerAccessibility();
        }
      });

      this.drawer.addEventListener("click", e => e.stopImmediatePropagation());
      this.hotspots.forEach(h => h.addEventListener("click", e => e.stopImmediatePropagation()));
    }

    setupAccessibilityClose() {
      document.addEventListener("keydown", e => {
        if ((this.drawer.classList.contains("active") || this.drawer.classList.contains("expanded")) && e.key === "Escape") {
          this.setDrawerState("closed");
          this.closeDrawerAccessibility();
        }
      });
    }

    addDocClickHandler() {
      if (this._docClickHandlerAdded) return;
      setTimeout(() => {
        this._docClickHandler = (e) => {
          if (this._suppressDocClick) return;
          if (!this.contains(e.target)) {
            this.userInteracted = false;
            this.closeDrawer();
            this.closeDrawerAccessibility();
          }
        };
        document.addEventListener("click", this._docClickHandler, true);
        this._docClickHandlerAdded = true;
      }, 50);
    }

    removeDocClickHandler() {
      if (this._docClickHandler && this._docClickHandlerAdded) {
        document.removeEventListener("click", this._docClickHandler, true);
        this._docClickHandlerAdded = false;
      }
    }

    openDrawer() {
      this.clearVisibleTimer();
      this.setDrawerState('active');
      this.isGridView = false;
      if (this.viewAllButton && this.viewAllText) {
        this.viewAllButton.textContent = this.viewAllText;
      }
      this.userInteracted = true;
      this.addDocClickHandler();
    }

    expandDrawer() {
      this.clearVisibleTimer();
      this.setDrawerState('expanded');
      this.isGridView = true;
      if (this.viewAllButton && this.viewLessText) {
        this.viewAllButton.textContent = this.viewLessText;
      }
      this.userInteracted = true;
      this.addDocClickHandler();
    }

    closeDrawer() {
      this.clearVisibleTimer();
      this.setDrawerState('closed');
      this.isGridView = false;
      if (this.viewAllButton && this.viewAllText) {
        this.viewAllButton.textContent = this.viewAllText;
      }
      this.userInteracted = true;
      this.removeDocClickHandler();
    }

    resetDrawer() {
      this.clearVisibleTimer();
      this.closeDrawer();
      this.hotspots = this.querySelectorAll(".shop-the-look__hotspot");
      this.products = this.querySelectorAll(".shop-the-look__product");
      this.hotspots.forEach(h => h.classList.remove("active"));
      this.products.forEach(p => p.classList.remove("active"));
      this.activeHotspot = null;
      this.userInteracted = false;
      this.isGridView = false;
    }

    resetAllStates() {
      this.clearVisibleTimer();
      this.resetDrawer();
      if (this.viewAllButton && this.viewAllText) {
        this.viewAllButton.textContent = this.viewAllText;
      }
    }

    setDrawerState(state) {
      const states = ["visible", "active", "expanded"];
      this.drawer.classList.remove(...states);
      this.drawer.setAttribute("data-drawer-state", state);

      if (state !== "closed" && states.includes(state)) {
        this.drawer.classList.add(state);
        this.drawer.style.pointerEvents = "auto";
      } else {
        this.drawer.style.pointerEvents = "none";
        this.userInteracted = false;
      }
    }

    openDrawerAccessibility(triggerEl) {
      this.lastFocusedElement = triggerEl || document.activeElement;
      const focusable = this.drawer.querySelector('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])');
      focusable?.focus({ preventScroll: true });
      this.drawer.addEventListener("keydown", this.handleFocusTrap);
    }

    closeDrawerAccessibility() {
      if (this.lastFocusedElement) this.lastFocusedElement.focus({ preventScroll: true });
      this.drawer.removeEventListener("keydown", this.handleFocusTrap);
    }

    forceCenterViewAll() {
      if (!this.isMobile) return;
      const viewAll = this.viewAllContainer;
      if (viewAll) {
        viewAll.style.display = "flex";
        viewAll.style.justifyContent = "center";
        viewAll.style.alignItems = "center";
        viewAll.style.width = "100%";
        viewAll.style.margin = "0";
      }
    }

    setupMobileSwipeToClose() {
      let startY = null;
      let currentY = null;
      let threshold = 60;
    
      const onTouchStart = (e) => {
        if (e.touches.length !== 1) return;
        startY = e.touches[0].clientY;
        currentY = startY;
      };
    
      const onTouchMove = (e) => {
        if (startY === null) return;
        currentY = e.touches[0].clientY;
        const deltaY = currentY - startY;
        if (deltaY > 10) {
          // Prevent browser from scrolling
          e.preventDefault();
        }
      };
    
      const onTouchEnd = (e) => {
        if (startY === null || currentY === null) return;
        const deltaY = currentY - startY;
        if (deltaY > threshold) {
          this.closeDrawer();
          this.closeDrawerAccessibility();
          this.forceCenterViewAll();
        }
        startY = null;
        currentY = null;
      };
    
      // Attach listeners with passive: false where needed
      this.drawer.addEventListener("touchstart", onTouchStart, { passive: true });
      this.drawer.addEventListener("touchmove", onTouchMove, { passive: false }); // 👈 Important
      this.drawer.addEventListener("touchend", onTouchEnd, { passive: true });
    }
    
    startVisibleTimer() {
      this.clearVisibleTimer();
      this._visibleTimeout = setTimeout(() => {
        // Only close if still visible and not interacted
        if (this.drawer.getAttribute('data-drawer-state') === 'visible' && !this.userInteracted) {
          this.setDrawerState('closed');
        }
        this._visibleTimeout = null;
      }, this._visibleTimerValue);
    }

    clearVisibleTimer() {
      if (this._visibleTimeout) {
        clearTimeout(this._visibleTimeout);
        this._visibleTimeout = null;
      }
    }
  }

  customElements.define("shop-the-look", ShopTheLook);
})();
